<script setup>
import { ref, onMounted, onBeforeUnmount, watch } from 'vue';
import { RouterView } from 'vue-router';
import SideMenu from './components/SideMenu.vue';
import { useFrontegg, useFronteggAuthGuard } from '@frontegg/vue';
import { useAuthStore } from './stores/auth.store';
import { WebSocketService, SentryService } from './services';
import _ from 'lodash';
import { useSourcesStore } from './stores/sources.store';
import { useDestinationsStore } from './stores/destinations.store';
import { usePipelinesStore } from './stores/pipelines.store';
import { useUserStore } from './stores/user.store';
import { useNovuStore } from './stores/novu.store';
import { useServicesStore } from './stores/services.store';
import AppLoader from './components/AppLoader.vue';
import Config from './utils/config';
import { userHasAccess } from './utils/utils';
import { Permissions } from './utils/constants';
import hubspotService from './services/hubspot.service';
import { DialogsWrapper } from 'vuejs-confirm-dialog';

// Ensure Frontegg auth guard is applied
useFronteggAuthGuard();

// Get our stores and Frontegg state
const authStore = useAuthStore();
const sourcesStore = useSourcesStore();
const destinationsStore = useDestinationsStore();
const pipelinesStore = usePipelinesStore();
const userStore = useUserStore();
const novuStore = useNovuStore();
const servicesStore = useServicesStore();
const { fronteggLoaded, authState } = useFrontegg();

// Local reactive state
const firstLoad = ref(true);
const loading = ref(false);
const mobileSidebar = ref(false); // Additional reactive property for mobile sidebar

// Mimic the original created() hook:
authStore.updateState(authState);

// Set up a beforeunload listener to close the WebSocket connection on browser unload
const handleBeforeUnload = () => {
    WebSocketService.close();
};

onMounted(() => {
    window.addEventListener('beforeunload', handleBeforeUnload);

    // Hide the HubSpot chat iframe if it appears
    const hideChat = () => {
        const element = document.getElementById('hubspot-messages-iframe-container');
        if (element) {
            element.style.setProperty('display', 'none', 'important');
        }
        return element;
    };

    const interval = setInterval(() => {
        const element = hideChat();
        if (element && element.style.display === 'none') {
            clearInterval(interval);
        }
    }, 500);
});

onBeforeUnmount(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload);
});

// New function to toggle the mobile sidebar (added from the branch)
function toggleMobileSidebar(value) {
    console.log('toggleMobileSidebar', value);
    mobileSidebar.value = value;
}

async function preloadConnectorConfigurations() {
    try {
        await sourcesStore.fetchConnectors();
        await destinationsStore.fetchConnectors();
    } catch (error) {
        console.warn('Failed to preload connector information:', error);
    }
}

// Watch for changes in the authState (specifically accessToken and isAuthenticated)
// to replicate the logic previously in updated()
watch(
    () => [_.get(authState, 'user.accessToken', null), authState.isAuthenticated, authState.user],
    async ([newAccessToken, isAuthenticated, newUser], oldValues) => {
        const [oldAccessToken, oldIsAuthenticated, oldUser] = oldValues || [null, false, null];

        // Handle token changes - reset stores if token changed
        if (authStore.accessToken !== newAccessToken && newAccessToken) {
            console.log('Access token updated, refreshing stores');
            // Reset the various store values if the token changes
            pipelinesStore.pipelines = undefined;
            sourcesStore.sources = undefined;
            destinationsStore.destinations = undefined;
            userStore.tenants = undefined;
            firstLoad.value = true;
        }

        // Always update the auth store with latest state
        authStore.updateState(authState);

        // Handle authentication state changes
        if (!oldIsAuthenticated && isAuthenticated) {
            console.log('User authenticated, initializing app');
            firstLoad.value = true;
        } else if (oldIsAuthenticated && !isAuthenticated) {
            console.log('User logged out, cleaning up');
            // Clean up on logout
            WebSocketService.close();
            firstLoad.value = true;
        }

        // When authenticated and it's the first load, perform the initial data fetching and setup
        if (isAuthenticated && firstLoad.value && newUser) {
            try {
                firstLoad.value = false;
                loading.value = true;

                userStore.fetchTenantTree();

                window.pylon = {
                    chat_settings: {
                        app_id: 'baaa4911-2fd1-4c3a-bc59-ab905b8c63a4',
                        name: `${authStore.user.firstName} ${authStore.user.lastName}`,
                        email: authStore.user.email,
                        avatar_url: authStore.user.profilePictureUrl
                    }
                };

                window.pylon.debug = true;

                window.analytics.identify(authStore.user.id, {
                    email: authStore.user.email,
                    name: `${authStore.user.firstName} ${authStore.user.lastName}`,
                    company: {
                        id: authState?.tenantsState?.activeTenant?.tenantId,
                        name: authState?.tenantsState?.activeTenant?.name
                    }
                });

                window.Appcues.identify(authStore.user.id, {
                    firstName: authStore.user.firstName,
                    lastName: authStore.user.lastName,
                    companyName: authState?.tenantsState?.activeTenant?.name,
                    email: authStore.user.email
                });

                if (Config.app.isDev) {
                    // const localAuthStore = this.authStore;
                    // const localUserStore = this.userStore;
                    // const localWindow = window;
                    // this.userStore.fetchCelloToken().then(async (celloInfo) => {
                    //     window.cello.cmd.push(async function (cello) {
                    //         try {
                    //             await cello.boot({
                    //                 productId: celloInfo.product_id,
                    //                 token: celloInfo.token,
                    //                 language: 'en',
                    //                 productUserDetails: {
                    //                     firstName: localAuthStore.user.firstName,
                    //                     lastName: localAuthStore.user.lastName,
                    //                     fullName: ${localAuthStore.user.firstName} ${localAuthStore.user.lastName},
                    //                     email: localAuthStore.user.email
                    //                 }
                    //             });
                    //             const referrerUCC = localWindow.CelloAttribution('getReferral');
                    //             if (referrerUCC) {
                    //                 localUserStore.sendCelloReferrerUCC(referrerUCC);
                    //             }
                    //             console.log('Cello booted successfully');
                    //         } catch (error) {
                    //             console.error('Failed to boot cello:', error);
                    //         }
                    //     });
                    // });
                }

                if (userHasAccess([Permissions.READ_SERVICES], authStore.user)) {
                    servicesStore.fetch();
                }

                // Preload connector configurations for global config access
                preloadConnectorConfigurations();

                await novuStore.initializeService();
                await Promise.allSettled([SentryService.init(), WebSocketService.init()]);

                if (!Config.app.isDev) {
                    hubspotService.init();
                }
            } catch (error) {
                console.error('Error during app initialization:', error);
            } finally {
                // Ensure both firstLoad and loading are reset
                firstLoad.value = false;
                loading.value = false;
            }
        }
    },
    { immediate: true }
);
</script>

<template>
    <div v-if="authState?.isAuthenticated && fronteggLoaded" id="app" class="min-h-screen bg-gray-100 overflow-auto">
        <SideMenu :mobile-sidebar-enabled="mobileSidebar">
            <RouterView v-slot="{ Component }">
                <AppLoader :force-loading="firstLoad || loading" default-height="100vh">
                    <template v-if="Component && !loading">
                        <Suspense>
                            <component :is="Component" @toggle-mobile-sidebar="toggleMobileSidebar" />
                        </Suspense>
                    </template>
                </AppLoader>
            </RouterView>
        </SideMenu>
    </div>
    <DialogsWrapper />
</template>

<style>
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
